import { motion } from 'framer-motion';
import { Sparkles, Star } from 'lucide-react';
import { BottomSheet, Button, useToast } from '../components';
import { useActions, useAppData } from '../data';
import { stellarAccent } from '../tokens';
import { STELLAR_ACCENT_KEY } from '../lib/theme';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * "Enjoying Margin?" prompt with the Stellar-theme reward. Three exits:
 *
 *   - Rate & unlock — marks rated, opens the (eventual) store URL on
 *     mobile, toasts the unlock + a hint to apply the theme.
 *   - Maybe later   — snoozes for 3 days.
 *   - Stop asking   — only rendered after at least one snooze; flips
 *     the rateNever flag so the auto-trigger never fires again.
 */
export function RatePrompt({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();

  // The "Stop asking" affordance only shows up once the user has
  // already snoozed at least once.
  const showStopAsking = !!data.rateSnoozed;

  const handleRate = () => {
    actions.markRated();
    // Hint the user that Stellar is now available without auto-applying
    // — they may still prefer their current accent.
    toast.show({
      message: 'Thanks! Stellar theme unlocked in Settings → Appearance',
      kind: 'success',
      duration: 4500,
    });
    if (typeof window !== 'undefined') {
      // Production app store URL slot. For now, the marketing page is
      // the best landing — both stores can be reached from there.
      window.open('https://marginbudget.com', '_blank');
    }
    onClose();
  };

  const handleSnooze = () => {
    actions.snoozeRate();
    toast.show({ message: 'We’ll check back in a few days', kind: 'info' });
    onClose();
  };

  const handleNever = () => {
    actions.neverRate();
    toast.show({ message: 'Got it. No more rate prompts', kind: 'info' });
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="px-card-pad pt-4 pb-card-pad flex flex-col items-center text-center gap-4">
        {/* Stellar-tinted star halo */}
        <div className="relative">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 250, damping: 22 }}
            className="w-20 h-20 rounded-pill inline-flex items-center justify-center shadow-md"
            style={{ backgroundColor: `rgb(${stellarAccent.light} / 0.15)` }}
          >
            <Star
              size={40}
              strokeWidth={1.5}
              fill={`rgb(${stellarAccent.light})`}
              color={`rgb(${stellarAccent.light})`}
            />
          </motion.div>
        </div>

        <div>
          <h2 className="text-[22px] font-semibold tracking-tight text-text-primary">
            Enjoying Margin?
          </h2>
          <p className="mt-2 text-[14px] text-text-secondary leading-relaxed max-w-[320px]">
            Rate us and unlock the exclusive{' '}
            <span
              className="font-semibold"
              style={{ color: `rgb(${stellarAccent.light})` }}
            >
              Stellar
            </span>{' '}
            accent: a premium violet found nowhere else in the app.
          </p>
        </div>

        {/* Stellar swatch preview row */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-pill bg-surface-secondary">
          <span
            className="w-6 h-6 rounded-pill shadow-sm inline-flex items-center justify-center text-white"
            style={{ backgroundColor: `rgb(${stellarAccent.light})` }}
          >
            <Sparkles size={12} strokeWidth={2} />
          </span>
          <span
            className="text-[12px] font-medium"
            style={{ color: `rgb(${stellarAccent.light})` }}
          >
            Stellar · {STELLAR_ACCENT_KEY}
          </span>
        </div>

        <div className="w-full flex flex-col gap-2 mt-2">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleRate}
            className="font-semibold"
          >
            Rate &amp; unlock
          </Button>
          <Button variant="ghost" size="lg" fullWidth onClick={handleSnooze}>
            Maybe later
          </Button>
          {showStopAsking ? (
            <button
              type="button"
              onClick={handleNever}
              className="text-[13px] text-text-tertiary py-3 min-h-tap"
            >
              Stop asking me
            </button>
          ) : null}
        </div>
      </div>
    </BottomSheet>
  );
}
